import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { showProQuestSearch } from "config";

import { useSearchCredentials } from "../hooks";

const MASKED_VALUE = "••••••••";

const allProviders = [
  {
    credentialType: "ebsco",
    title: "EBSCO",
    introduction: "If you are an existing EBSCO customer and know your EBSCO API credentials for Sooth.fyi, enter them below.",
    usernameLabel: "EBSCO API User ID",
    passwordLabel: "EBSCO API Password",
    profileLabel: "EBSCO Profile ID",
    description:
      "EBSCO is the leading provider of research databases, e-journals, magazine subscriptions, ebooks and discovery service for academic libraries, public libraries, corporations, schools, government and medical institutions",
  },
  {
    credentialType: "proQuest",
    title: "ProQuest",
    introduction: "If you are an existing ProQuest customer and know your ProQuest Federated Search credentials for Sooth.fyi, enter them below.",
    usernameLabel: "ProQuest Federated Search Username",
    passwordLabel: "ProQuest Federated Search Password",
    description:
      "ProQuest is a leading global information and technology company that provides research databases, tools, and digital content to academic, public, school, and corporate libraries.",
  },
];

const providers = allProviders.filter(
  (provider) => provider.credentialType !== "proQuest" || showProQuestSearch
);

function maskValue(value) {
  return value ? MASKED_VALUE : "Not provided";
}

function ProviderCredentialSection({
  provider,
  credential,
  isSaving,
  onSave,
}) {
  const [username, setUsername] = useState(credential.username);
  const [profile, setProfile] = useState(credential.profile);
  const [password, setPassword] = useState("");
  const [isEditing, setIsEditing] = useState(!credential.hasSavedCredential);
  const [error, setError] = useState(null);

  useEffect(() => {
    setUsername(credential.username);
    setProfile(credential.profile);
    if (!credential.hasSavedCredential) setIsEditing(true);
  }, [credential.username, credential.profile, credential.hasSavedCredential]);

  const fieldsLocked = credential.hasSavedCredential && !isEditing;
  const canSave =
    !fieldsLocked &&
    username.trim() &&
    !isSaving &&
    (credential.hasSavedCredential || password.trim());

  async function handleSave() {
    setError(null);
    const response = await onSave({
      credentialType: provider.credentialType,
      username: username.trim(),
      password: password.trim(),
      profile: provider.profileLabel ? profile.trim() || null : null,
      isEnabled: true,
    });

    if (response.success) {
      setPassword("");
      setIsEditing(false);
      toast.success(`${provider.title} credentials saved and verified.`);
    } else {
      setError(response.error);
    }
  }

  async function handleToggle(_event, enabled) {
    setError(null);
    const response = await onSave({
      credentialType: provider.credentialType,
      username: credential.username,
      password: "",
      profile: provider.profileLabel ? credential.profile || null : null,
      isEnabled: enabled,
    });

    if (response.success) {
      toast.success(
        `${provider.title} is now ${enabled ? "enabled" : "disabled"}.`
      );
    } else {
      setError(response.error);
    }
  }

  return (
    <Stack spacing={1.5} py={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography component="h2" variant="h4">
          {provider.title}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>Off</Typography>
          <Tooltip
            title={
              credential.hasSavedCredential
                ? "Enable or disable this search provider for the group."
                : `Save valid ${provider.title} credentials before enabling this provider.`
            }
          >
            <span>
              <Switch
                checked={credential.isEnabled}
                onChange={handleToggle}
                disabled={!credential.hasSavedCredential || isSaving}
              />
            </span>
          </Tooltip>
          <Typography>On</Typography>
        </Stack>
      </Stack>

      <Typography>{provider.description}</Typography>
      <Typography>{provider.introduction}</Typography>

      {fieldsLocked ? (
        <Stack spacing={0.5} maxWidth="sm">
          <Typography>
            <strong>{provider.usernameLabel}:</strong> {maskValue(credential.username)}
          </Typography>
          <Typography>
            <strong>{provider.passwordLabel}:</strong> {MASKED_VALUE}
          </Typography>
          {provider.profileLabel && (
            <Typography>
              <strong>{provider.profileLabel}:</strong> {maskValue(credential.profile)}
            </Typography>
          )}
          <Box>
            <Tooltip title={`Edit ${provider.title} credentials`}>
              <IconButton
                aria-label={`Edit ${provider.title} credentials`}
                onClick={() => setIsEditing(true)}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={1.5} maxWidth="sm">
          <TextField
            label={provider.usernameLabel}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={isSaving}
            required
            fullWidth
          />
          <TextField
            label={provider.passwordLabel}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={credential.hasSavedCredential ? MASKED_VALUE : ""}
            helperText={
              credential.hasSavedCredential
                ? "Leave blank to keep the current password."
                : ""
            }
            disabled={isSaving}
            required={!credential.hasSavedCredential}
            fullWidth
          />
          {provider.profileLabel && <TextField
            label={provider.profileLabel}
            value={profile}
            onChange={(event) => setProfile(event.target.value)}
            disabled={isSaving}
            fullWidth
          />}
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Button type="button" onClick={handleSave} disabled={!canSave}>
              {isSaving ? "Verifying..." : "Save and Verify"}
            </Button>
          </Box>
        </Stack>
      )}

      {provider.credentialType === "ebsco" ? (
        <Stack spacing={1.5}>
          <Typography>
            If you are an existing EBSCO customer but don't know your institution's
            API credentials for Sooth.fyi, contact EBSCO support and request an{" "}
            <Box component="span" sx={{ textDecoration: "underline" }}>API profile for use with Sooth.fyi</Box>.
            You can contact EBSCO support using{" "}
            <Link underline="always" href="https://connect.ebsco.com/s/contactsupport" target="_blank" rel="noopener noreferrer">this link</Link>{" "}
            (note: EBSCO login credentials are required to connect with their support).
          </Typography>
          <Typography>
            To learn more about how this integration works, you can read EBSCO's documentation{" "}
            <Link href="#" underline="always">here</Link>.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Typography>
            If you are an existing ProQuest customer but don't know your institution's
            Federated Search credentials for Sooth.fyi, contact their support via email at{" "}
            <Link underline="always" href="mailto:email.technicalsupport@proquest.com">email.technicalsupport@proquest.com</Link>{" "}
            and request your{" "}
            <Box component="span" sx={{ textDecoration: "underline" }}>Federated Search credentials for Sooth.fyi</Box>.
            In your email, you must include your school name so they can identify your account quickly.
          </Typography>
          <Typography>
            If you get your ProQuest access indirectly through your state or a similar
            type of program, you may also be eligible for Federated Search credentials
            for Sooth.fyi. If this applies to you, please follow the instructions above
            and also include which state or program through which you currently receive ProQuest access.
          </Typography>
        </Stack>
      )}
      {/* Documentation and sales URLs are pending in updates.pdf. */}
        <Typography>
          If you are not an existing {provider.title} customer and would like to explore
          a subscription (or upgrade an existing one), you can contact their sales team{" "}
          <Link href="#" underline="always">here</Link>.
        </Typography>
    </Stack>
  );
}

export default function DatabaseIntegration() {
  const { credentials, isLoading, savingType, saveCredential } =
    useSearchCredentials();

  if (isLoading) {
    return <CircularProgress aria-label="Loading search credentials" />;
  }

  return (
    <Stack pl={3}>
      {providers.map((provider, index) => (
        <div key={provider.credentialType}>
          {index > 0 && <Divider />}
          <ProviderCredentialSection
            provider={provider}
            credential={credentials[provider.credentialType]}
            isSaving={savingType === provider.credentialType}
            onSave={saveCredential}
          />
        </div>
      ))}
    </Stack>
  );
}
